// scrollLock — the ONE way anything holds the room still (a sheet, a menu, a
// film, the onboarding flow).
//
// Every surface used to lock the page by hand: save `document.body.style.overflow`,
// set "hidden", put the saved value back on close. Two of them open at once and
// closed in the other order (a sheet over a guide, a menu over a sheet) left the
// page locked for good: the second to close restored the "hidden" the first had
// written. The room stopped scrolling and the navigator stopped answering until
// a reload.
//
// Here the lock is COUNTED: the first holder saves the page's own values, the
// last one to let go puts them back, and nobody in between touches them.
// `lockScroll()` returns its release; calling a release twice is harmless.
//
//   useEffect(() => (open ? lockScroll("sheet") : undefined), [open]);

type Saved = { body: string; html: string; bodyPad: string };

let holders = new Map<number, string>();
let nextId = 1;
let saved: Saved | null = null;

function apply() {
  if (typeof document === "undefined") return;
  const body = document.body;
  const html = document.documentElement;
  if (holders.size > 0) {
    if (!saved) {
      saved = { body: body.style.overflow, html: html.style.overflow, bodyPad: body.style.paddingRight };
      // the scrollbar's width is given back as padding, so the room does not shift sideways
      const gap = window.innerWidth - html.clientWidth;
      if (gap > 0) body.style.paddingRight = `${gap}px`;
    }
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    html.setAttribute("data-scroll-locked", "");
  } else if (saved) {
    body.style.overflow = saved.body;
    html.style.overflow = saved.html;
    body.style.paddingRight = saved.bodyPad;
    html.removeAttribute("data-scroll-locked");
    saved = null;
  }
}

/** Hold the page still. Returns the release (idempotent). `who` names the holder for debugging. */
export function lockScroll(who = "anon"): () => void {
  const id = nextId++;
  holders.set(id, who);
  apply();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders.delete(id);
    apply();
  };
}

// ── gestures: only the sheet on top scrolls ─────────────────────────────────
//
// While a sheet is up, a wheel or a touch drag that is not over the TOP sheet's
// own scroller is swallowed, so the page behind never moves under it. One
// listener for every sheet (counted like the lock): each sheet used to install
// its own, and a lower sheet swallowed every wheel meant for the one stacked on
// it. The top sheet is the last `[data-sheet]` in the document (portals append
// in opening order); its scrollers carry `[data-sheet-scroll]`, a sideways strip
// inside it `[data-hscroll]`.

let gestureHolders = 0;

function insideTopSheet(t: EventTarget | null): boolean {
  if (typeof document === "undefined") return true;
  const el = t instanceof Element ? t : t instanceof Node ? t.parentElement : null;
  if (!el) return false;
  // the top layer: the last sheet, or a layer opened over one (the share
  // studio, a menu: `[data-gesture-root]`), which owns every gesture inside it
  const layers = document.querySelectorAll("[data-sheet], [data-gesture-root]");
  const top = layers[layers.length - 1];
  if (!top) return true;
  if (!top.hasAttribute("data-sheet")) return top.contains(el);
  const scroller = el.closest("[data-sheet-scroll], [data-hscroll]");
  return !!scroller && top.contains(scroller);
}

function swallow(e: Event) {
  if (!insideTopSheet(e.target)) e.preventDefault();
}

/** Swallow page gestures that are not over the top sheet's scroller. Returns the release (idempotent). */
export function holdGestures(): () => void {
  if (typeof document === "undefined") return () => {};
  gestureHolders++;
  if (gestureHolders === 1) {
    document.addEventListener("wheel", swallow, { passive: false });
    document.addEventListener("touchmove", swallow, { passive: false });
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    gestureHolders = Math.max(0, gestureHolders - 1);
    if (gestureHolders === 0) {
      document.removeEventListener("wheel", swallow);
      document.removeEventListener("touchmove", swallow);
    }
  };
}

// ── a sheet is up ────────────────────────────────────────────────────────────
//
// `html[data-sheet-open]` while any sheet is on screen (counted), so the room's
// own corners can step aside: the Ask netti corner pauses its pointer field,
// the dashboard's share bar hands over to the sheet's own.

let sheetHolders = 0;

/** Say a sheet is up. Returns the release (idempotent). */
export function markSheetOpen(): () => void {
  if (typeof document === "undefined") return () => {};
  sheetHolders++;
  document.documentElement.setAttribute("data-sheet-open", "");
  let released = false;
  return () => {
    if (released) return;
    released = true;
    sheetHolders = Math.max(0, sheetHolders - 1);
    if (sheetHolders === 0) document.documentElement.removeAttribute("data-sheet-open");
  };
}

/** Is a sheet on screen right now? */
export function sheetIsOpen(): boolean {
  return sheetHolders > 0;
}

/** Who holds the page right now (debugging, tests). */
export function scrollHolders(): string[] {
  return Array.from(holders.values());
}

/** Tests only: forget every holder and the saved values. */
export function resetScrollLockForTests(): void {
  holders = new Map();
  saved = null;
  nextId = 1;
  gestureHolders = 0;
  sheetHolders = 0;
}

export { insideTopSheet as _insideTopSheetForTests };
